import React, { useState, useEffect } from 'react';
import { BarChart3, Users, Cpu, DollarSign, TrendingUp, AlertTriangle, Activity, Zap, Database, Shield, RefreshCw } from 'lucide-react';
import { authenticatedFetchJSON } from '../../utils/authenticatedFetch';
import type {
    AnalyticsSection,
    RateLimitAlert,
    PlatformAnalyticsResponse
} from '../../types';

const ICON_MAP: Record<string, React.FC<{ size?: number; className?: string }>> = {
    Shield,
    Cpu,
    DollarSign,
    TrendingUp,
    Zap,
};

const PlatformAnalyticsPage: React.FC = () => {
    const [data, setData] = useState<PlatformAnalyticsResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<string>('');

    const fetchAnalytics = async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await authenticatedFetchJSON<PlatformAnalyticsResponse>('/api/admin/analytics/summary');
            setData(result);
            setLastUpdated(new Date(result.last_updated).toLocaleTimeString());
        } catch (err) {
            console.error('Failed to fetch analytics:', err);
            setError('Failed to load analytics. Backend may be unavailable.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const handleToggleMetric = async (section: string, metricId: string, currentEnabled: boolean) => {
        try {
            await authenticatedFetchJSON('/api/admin/analytics/toggle', {
                method: 'PUT',
                body: JSON.stringify({ section, metric_id: metricId, enabled: !currentEnabled })
            });
            fetchAnalytics();
        } catch (err) {
            console.error('Failed to toggle metric:', err);
        }
    };

    const handleToggleRateLimit = async (id: string, currentEnabled: boolean) => {
        try {
            await authenticatedFetchJSON('/api/admin/analytics/rate-limit', {
                method: 'PUT',
                body: JSON.stringify({ id, enabled: !currentEnabled })
            });
            fetchAnalytics();
        } catch (err) {
            console.error('Failed to toggle rate limit:', err);
        }
    };

    const getColorClasses = (color: string) => {
        const colors: Record<string, { bg: string; text: string; border: string }> = {
            blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
            purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200' },
            emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
            amber: { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
            cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200' },
        };
        return colors[color] || colors.blue;
    };

    if (loading && !data) {
        return (
            <div className="p-8 flex items-center justify-center">
                <RefreshCw className="animate-spin text-blue-500" size={32} />
                <span className="ml-3 text-slate-500">Loading Platform Analytics...</span>
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8 text-center">
                <AlertTriangle className="mx-auto text-red-500 mb-4" size={48} />
                <h2 className="text-xl font-bold text-slate-800 mb-2">Failed to Load Analytics</h2>
                <p className="text-slate-500 mb-4">{error}</p>
                <button onClick={fetchAnalytics} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Retry
                </button>
            </div>
        );
    }

    const summary = data?.summary;
    const sections = data?.sections || {};
    const rateLimits = data?.rate_limits || [];

    return (
        <div className="p-6 space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-3">
                        <BarChart3 className="text-blue-600" size={32} />
                        Platform Analytics
                    </h1>
                    <p className="text-slate-500 mt-1">
                        Monitor customer usage, AI costs, revenue metrics, and system health across the platform.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={fetchAnalytics}
                        disabled={loading}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 transition-colors disabled:opacity-50"
                        title="Refresh Data"
                    >
                        <RefreshCw className={`text-slate-600 ${loading ? 'animate-spin' : ''}`} size={18} />
                    </button>
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-bold rounded-full flex items-center gap-1">
                        <Activity size={14} /> Live
                    </span>
                    <span className="text-sm text-slate-400">Last updated: {lastUpdated}</span>
                </div>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <SummaryCard icon={<Users size={24} />} value={summary.active_users_today.toLocaleString()} label="Active Users Today" trend={summary.active_users_trend} color="blue" />
                    <SummaryCard icon={<Cpu size={24} />} value={`${(summary.ai_calls_today / 1000).toFixed(1)}K`} label="AI Calls Today" trend={summary.ai_calls_trend} color="purple" />
                    <SummaryCard icon={<DollarSign size={24} />} value={`$${(summary.mrr / 1000).toFixed(1)}K`} label="Monthly Recurring" trend={summary.mrr_trend} color="emerald" />
                    <SummaryCard icon={<Database size={24} />} value={`${(summary.tokens_today / 1000000).toFixed(1)}M`} label="Tokens Used Today" trend={summary.tokens_trend} color="amber" />
                </div>
            )}

            {/* Analytics Sections */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Object.entries(sections).map(([sectionName, section]) => (
                    <AnalyticsSectionCard
                        key={sectionName}
                        sectionName={sectionName}
                        section={section}
                        getColorClasses={getColorClasses}
                        onToggle={handleToggleMetric}
                    />
                ))}
            </div>

            {/* Rate Limits & Alerts Section */}
            <RateLimitsTable rateLimits={rateLimits} onToggle={handleToggleRateLimit} />

            {/* Footer Note */}
            <div className="text-center text-xs text-slate-400 py-4">
                Platform analytics are only visible to AutoGain staff. Customer data is aggregated and anonymized.
                <br />
                <span className="text-slate-300">API: /api/admin/analytics/summary</span>
            </div>
        </div>
    );
};

// Summary Card Component
const SummaryCard: React.FC<{ icon: React.ReactNode; value: string; label: string; trend: string; color: string }> = ({ icon, value, label, trend, color }) => {
    const gradients: Record<string, string> = {
        blue: 'from-blue-500 to-blue-600',
        purple: 'from-purple-500 to-purple-600',
        emerald: 'from-emerald-500 to-emerald-600',
        amber: 'from-amber-500 to-orange-500',
    };
    const textColors: Record<string, string> = {
        blue: 'text-blue-100',
        purple: 'text-purple-100',
        emerald: 'text-emerald-100',
        amber: 'text-amber-100',
    };
    return (
        <div className={`bg-gradient-to-br ${gradients[color]} rounded-xl p-5 text-white shadow-lg`}>
            <div className="flex items-center justify-between">
                {icon}
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">{trend}</span>
            </div>
            <div className="mt-4">
                <div className="text-3xl font-bold">{value}</div>
                <div className={`${textColors[color]} text-sm`}>{label}</div>
            </div>
        </div>
    );
};

// Analytics Section Card Component
const AnalyticsSectionCard: React.FC<{
    sectionName: string;
    section: AnalyticsSection;
    getColorClasses: (color: string) => { bg: string; text: string; border: string };
    onToggle: (section: string, metricId: string, enabled: boolean) => void;
}> = ({ sectionName, section, getColorClasses, onToggle }) => {
    const IconComponent = ICON_MAP[section.icon] || Shield;
    const colors = getColorClasses(section.color);

    return (
        <div className={`bg-white rounded-xl border ${colors.border} shadow-sm overflow-hidden hover:shadow-md transition-shadow`}>
            <div className={`${colors.bg} px-5 py-3 border-b ${colors.border} flex items-center justify-between`}>
                <div className="flex items-center gap-2">
                    <IconComponent className={colors.text} size={20} />
                    <h3 className={`font-bold ${colors.text}`}>{sectionName}</h3>
                </div>
                <span className="text-xs text-slate-400">
                    {section.metrics.filter(m => m.enabled).length}/{section.metrics.length} enabled
                </span>
            </div>
            <div className="divide-y divide-slate-100">
                {section.metrics.map(metric => (
                    <div key={metric.id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50">
                        <div className="flex-1">
                            <div className="font-medium text-slate-700 flex items-center gap-2">
                                {metric.name}
                                {!metric.enabled && <span className="text-[10px] bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">DISABLED</span>}
                            </div>
                            <div className="text-xs text-slate-400">{metric.description}</div>
                        </div>
                        <div className="text-right">
                            <div className="font-bold text-slate-800">{metric.value}</div>
                            <div className={`text-xs ${metric.trend.startsWith('+') ? 'text-green-600' : metric.trend.startsWith('-') ? 'text-red-600' : 'text-slate-400'}`}>
                                {metric.trend}
                            </div>
                        </div>
                        <div className="ml-4">
                            <ToggleButton enabled={metric.enabled} onClick={() => onToggle(sectionName, metric.id, metric.enabled)} color="blue" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// Rate Limits Table Component
const RateLimitsTable: React.FC<{ rateLimits: RateLimitAlert[]; onToggle: (id: string, enabled: boolean) => void }> = ({ rateLimits, onToggle }) => (
    <div className="bg-white rounded-xl border border-red-200 shadow-sm overflow-hidden">
        <div className="bg-red-50 px-5 py-3 border-b border-red-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <AlertTriangle className="text-red-600" size={20} />
                <h3 className="font-bold text-red-600">Rate Limits & Alerts (Runaway Protection)</h3>
            </div>
            <span className="text-xs text-red-500">{rateLimits.filter(r => r.enabled).length}/{rateLimits.length} alerts enabled</span>
        </div>
        <div className="p-4">
            <p className="text-sm text-slate-500 mb-4">Configure thresholds to detect bugs, misconfigurations, or abuse.</p>
            <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 uppercase border-b">
                    <tr>
                        <th className="text-left py-2 px-2">Metric</th>
                        <th className="text-center py-2 px-2">Current</th>
                        <th className="text-center py-2 px-2">Threshold</th>
                        <th className="text-center py-2 px-2">Status</th>
                        <th className="text-center py-2 px-2">Severity</th>
                        <th className="text-center py-2 px-2">Enabled</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {rateLimits.map(alert => {
                        const percentage = (alert.current / alert.threshold) * 100;
                        const isWarning = percentage > 70;
                        const isCritical = percentage > 90;
                        return (
                            <tr key={alert.id} className="hover:bg-slate-50">
                                <td className="py-3 px-2">
                                    <div className="font-medium text-slate-700">{alert.name}</div>
                                    <div className="text-xs text-slate-400">{alert.description}</div>
                                </td>
                                <td className="py-3 px-2 text-center">
                                    <span className={`font-mono font-bold ${isCritical ? 'text-red-600' : isWarning ? 'text-amber-600' : 'text-slate-700'}`}>
                                        {alert.current.toLocaleString()}
                                    </span>
                                </td>
                                <td className="py-3 px-2 text-center font-mono text-slate-500">
                                    {alert.threshold.toLocaleString()}{alert.unit}
                                </td>
                                <td className="py-3 px-2">
                                    <div className="w-full bg-slate-200 rounded-full h-2">
                                        <div
                                            className={`h-2 rounded-full transition-all ${isCritical ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-green-500'}`}
                                            style={{ width: `${Math.min(percentage, 100)}%` }}
                                        />
                                    </div>
                                    <div className="text-[10px] text-center text-slate-400 mt-0.5">{percentage.toFixed(0)}%</div>
                                </td>
                                <td className="py-3 px-2 text-center">
                                    <span className={`px-2 py-0.5 text-xs font-bold rounded ${alert.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                        {alert.severity.toUpperCase()}
                                    </span>
                                </td>
                                <td className="py-3 px-2 text-center">
                                    <ToggleButton enabled={alert.enabled} onClick={() => onToggle(alert.id, alert.enabled)} color="red" />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    </div>
);

// Toggle Button Component
const ToggleButton: React.FC<{ enabled: boolean; onClick: () => void; color: 'blue' | 'red' }> = ({ enabled, onClick, color }) => {
    const bgColors = { blue: 'bg-blue-600', red: 'bg-red-500' };
    return (
        <button
            onClick={onClick}
            className={`relative inline-flex h-5 w-9 items-center rounded-full cursor-pointer transition-colors ${enabled ? bgColors[color] : 'bg-slate-300'}`}
        >
            <span className={`${enabled ? 'translate-x-5' : 'translate-x-1'} inline-block h-3 w-3 transform rounded-full bg-white transition-transform`} />
        </button>
    );
};

export default PlatformAnalyticsPage;
